/*
 * Product gallery, custom slots (blocks/_product-media-gallery.liquid).
 *
 *   <bt-slide-video>  — a silent looping clip drawn over the slot picture, which stays as its poster.
 *   <bt-slide-rating> — the «4.8 ★ · 411 opinii» plate, numbers from the reviews API.
 *
 * The block loads this file only when a slot really uses one of the two; text captions need no JS.
 * Both are custom elements on purpose: the gallery markup is swapped on every variant change and the
 * slideshow moves slides around the DOM when it jumps, so they have to set themselves up wherever
 * and whenever they land.
 */
(function () {
  'use strict';

  if (window.__btGallerySlides) return;
  window.__btGallerySlides = true;

  /* ------------------------------------------------------------------ video */

  /* A slide counts as "the one on screen" once this much of it is visible. Covers both cases the
     clip must stop in: the visitor swiped to another slide and the gallery scrolled out of view. */
  var ACTIVE_RATIO = 0.6;
  /* A clip the visitor started by hand keeps playing until its slide is nearly gone. */
  var MANUAL_RATIO = 0.2;

  var players = [];

  function reducedMotion() {
    return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function savingData() {
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return false;
    return connection.saveData === true || /(^|-)2g$/.test(connection.effectiveType || '');
  }

  function autoplayWanted() {
    return !reducedMotion() && !savingData();
  }

  /* One clip at a time per gallery: the slide that is most on screen (matters in the desktop grid,
     where several slides are visible at once). */
  function arbitrate() {
    var winners = new Map();

    players.forEach(function (player) {
      if (player.manual && player.ratio < MANUAL_RATIO) player.manual = false;
      /* A tap-to-play clip never claims the gallery by itself: only after its button was pressed,
         or to carry on from where the visitor left it mid-play. */
      var selfStarting = !player.byClick || player.resumable;
      if (!player.manual && !(selfStarting && player.ratio >= ACTIVE_RATIO)) return;

      var best = winners.get(player.gallery);
      var better =
        !best || (player.manual && !best.manual) || (player.manual === best.manual && player.ratio > best.ratio);
      if (better) winners.set(player.gallery, player);
    });

    players.forEach(function (player) {
      player.setActive(!document.hidden && winners.get(player.gallery) === player);
    });
  }

  var observer = null;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          entry.target.ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
        });
        arbitrate();
      },
      { threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] }
    );
  }

  document.addEventListener('visibilitychange', arbitrate);

  class BtSlideVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('button');
      if (!this.video) return;

      this.gallery = this.closest('media-gallery') || document;
      this.ratio = 0;
      this.active = false;
      /* data-mode="click": the slide opens as its picture; the clip runs once when the visitor
         presses play, then the picture and the button come back. Default: starts by itself, loops. */
      this.byClick = this.getAttribute('data-mode') === 'click';

      if (!this.wired) {
        this.wired = true;
        this.manual = false;
        this.resumable = false;
        this.finished = false;
        this.refused = false;
        this.broken = false;
        this.video.muted = true;

        this.video.addEventListener('playing', () => {
          this.refused = false;
          this.pending = false;
          this.stalled = false;
          this.classList.add('is-started');
          this.refreshButton();
        });
        /* Ran out of data mid-play (slow connection): the button comes back as a spinner. */
        this.video.addEventListener('waiting', () => {
          if (this.video.paused || !this.classList.contains('is-started')) return;
          this.stalled = true;
          this.refreshButton();
        });
        this.video.addEventListener('pause', () => {
          this.stalled = false;
          this.refreshButton();
        });
        /* Fires only without `loop`, i.e. for tap-to-play clips: let go of the gallery. */
        this.video.addEventListener('ended', () => {
          this.manual = false;
          this.resumable = false;
          this.finished = true;
          arbitrate();
        });
        /* The file is gone or the browser cannot decode it: the poster stays, with no dead button. */
        this.video.addEventListener('error', () => {
          if (this.queue && this.queue.length) {
            this.video.src = this.queue.shift();
            if (this.active) this.start();
            return;
          }
          this.broken = true;
          this.refreshButton();
        });

        if (this.button) {
          this.button.addEventListener('click', (event) => {
            /* The slide itself may open the zoom dialog on click. */
            event.preventDefault();
            event.stopPropagation();
            /* Pressed once — the button no longer needs to call for attention (its CSS pulse stops). */
            this.classList.add('is-played');
            this.manual = true;
            arbitrate();
            this.start();
          });
          /* …and the slideshow starts a drag on mousedown anywhere inside it. */
          this.button.addEventListener('mousedown', (event) => event.stopPropagation());
        }
      }

      players.push(this);
      this.refreshButton();
      if (observer) observer.observe(this);
    }

    disconnectedCallback() {
      var index = players.indexOf(this);
      if (index !== -1) players.splice(index, 1);
      if (observer) observer.unobserve(this);
      if (!this.video) return;

      this.ratio = 0;
      this.setActive(false);
    }

    setActive(on) {
      var was = this.active;
      this.active = on;

      if (!on) {
        this.pending = false;
        this.stalled = false;
        if (!this.video.paused) this.video.pause();
        if (this.byClick && was) {
          /* Played to the end — the slide is its picture again. Left mid-play (swiped or scrolled
             away) — the clip keeps its place and carries on by itself when the slide is back. */
          if (this.finished) this.rewind();
          else this.resumable = true;
        }
        if (was) this.release();
        return;
      }
      if (was) return;

      if (this.manual || this.resumable || (!this.byClick && autoplayWanted())) this.start();
      this.refreshButton();
    }

    /* Tap-to-play, clip finished: back to the picture and the button; the next press starts over. */
    rewind() {
      this.finished = false;
      this.resumable = false;
      this.classList.remove('is-started');
      /* Older engines leave an ended clip "not paused"; seeking back would then restart it. */
      if (!this.video.paused) this.video.pause();
      if (this.video.readyState > 0) this.video.currentTime = 0;
      this.refreshButton();
    }

    /* A paused clip keeps downloading. A long one that is far from finished then takes the
       connection away from the clip on the next slide — so it lets go of the file and remembers
       its place; start() picks both up again. Short clips are complete by now and stay as they are. */
    release() {
      var video = this.video;
      if (!video.getAttribute('src') || !isFinite(video.duration)) return;

      var loaded = video.buffered.length ? video.buffered.end(video.buffered.length - 1) : 0;
      if (video.duration - loaded < 3) return;

      this.held = { src: video.currentSrc || video.getAttribute('src'), time: this.finished ? 0 : video.currentTime };
      video.removeAttribute('src');
      video.load();
      this.refreshButton();
    }

    /* Nothing is downloaded before this point: the <video> ships without a src. */
    start() {
      var video = this.video;

      if (!video.getAttribute('src') && this.held) {
        var held = this.held;
        this.held = null;
        video.src = held.src;
        if (held.time > 0) {
          video.addEventListener(
            'loadedmetadata',
            function () {
              video.currentTime = held.time;
            },
            { once: true }
          );
        }
      }

      if (!video.getAttribute('src')) {
        /* The uploaded file first, when the block asks for it; Shopify's own version is the fallback. */
        if (!this.queue) {
          this.queue = [this.getAttribute('data-original'), this.pickSource()].filter(Boolean);
        }
        var source = this.queue.shift();
        if (!source) return;
        video.src = source;
      }

      video.muted = true;
      /* Asked to play: from here to the first frame the button is a spinner (see refreshButton). */
      this.pending = true;
      this.refreshButton();

      var attempt;
      try {
        attempt = video.play();
      } catch (error) {
        attempt = Promise.reject(error);
      }
      if (!attempt || !attempt.then) return;

      attempt.then(
        () => {},
        (error) => {
          /* AbortError = we paused it ourselves while it was still loading, not a refusal. */
          if (error && error.name === 'AbortError') return;
          /* The file did not open: the `error` handler above moves on to the next one or gives up. */
          if (error && error.name === 'NotSupportedError') return;
          /* Autoplay is not allowed here (iOS Low Power Mode, browser policy): poster + play button. */
          this.refused = true;
          this.pending = false;
          this.refreshButton();
        }
      );
    }

    /* Smallest rendition that still covers the slide; Shopify transcodes every upload to 480/720/1080. */
    pickSource() {
      var sources = (this.getAttribute('data-sources') || '')
        .split(' ')
        .map(function (item) {
          var parts = item.split('|');
          return { url: parts[0], width: parseInt(parts[1], 10) || 0, height: parseInt(parts[2], 10) || 0 };
        })
        .filter(function (source) {
          return source.url;
        })
        .sort(function (a, b) {
          return a.width * a.height - b.width * b.height;
        });
      if (!sources.length) return '';

      var density = Math.min(window.devicePixelRatio || 1, 2);
      var width = this.clientWidth * density;
      var height = this.clientHeight * density;

      for (var i = 0; i < sources.length; i++) {
        if (sources[i].width >= width && sources[i].height >= height) return sources[i].url;
      }
      return sources[sources.length - 1].url;
    }

    /* The button is the fallback, not the default: it shows only when the clip will not start by
       itself — tap-to-play mode, reduced motion, data saver, or the browser refused to autoplay.
       On those slides a press must never look like nothing happened: until the first frame is on
       screen, and whenever the clip waits for data, the button stays as a loading spinner. Clips
       that start by themselves keep their poster and show no spinner. */
    refreshButton() {
      if (!this.button) return;
      var stuck = this.byClick || this.refused || !autoplayWanted() || !observer;
      var loading = stuck && !this.broken && !!(this.pending || this.stalled);
      this.button.hidden = this.broken || !(loading || (stuck && this.video.paused));
      this.button.classList.toggle('is-loading', loading);
      if (loading) this.button.setAttribute('aria-busy', 'true');
      else this.button.removeAttribute('aria-busy');
    }
  }

  if (window.matchMedia) {
    var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    var onMotionChange = function () {
      players.forEach(function (player) {
        player.refreshButton();
      });
    };
    if (motionQuery.addEventListener) motionQuery.addEventListener('change', onMotionChange);
  }

  /* ----------------------------------------------------------------- rating */

  /* Plural forms by Intl.PluralRules category. The count is live, so the word has to follow it. */
  var REVIEW_WORDS = {
    pl: { one: 'opinia', few: 'opinie', many: 'opinii', other: 'opinii' },
    en: { one: 'review', other: 'reviews' },
    de: { one: 'Bewertung', other: 'Bewertungen' },
    cs: { one: 'recenze', few: 'recenze', many: 'recenze', other: 'recenzí' },
    es: { one: 'opinión', many: 'opiniones', other: 'opiniones' },
    fr: { one: 'avis', many: 'avis', other: 'avis' },
    nl: { one: 'review', other: 'reviews' },
    sk: { one: 'recenzia', few: 'recenzie', many: 'recenzie', other: 'recenzií' },
    hu: { one: 'vélemény', other: 'vélemény' },
    ro: { one: 'recenzie', few: 'recenzii', other: 'de recenzii' },
    lt: { one: 'atsiliepimas', few: 'atsiliepimai', many: 'atsiliepimo', other: 'atsiliepimų' },
    lv: { zero: 'atsauksmes', one: 'atsauksme', other: 'atsauksmes' },
    et: { one: 'arvustus', other: 'arvustust' },
    it: { one: 'recensione', other: 'recensioni' },
    ru: { one: 'отзыв', few: 'отзыва', many: 'отзывов', other: 'отзыва' },
    uk: { one: 'відгук', few: 'відгуки', many: 'відгуків', other: 'відгуку' },
  };

  function reviewsWord(locale, count) {
    var language = String(locale || 'en')
      .toLowerCase()
      .split('-')[0];
    var forms = REVIEW_WORDS[language] || REVIEW_WORDS.en;
    var category = 'other';
    try {
      category = new Intl.PluralRules(language).select(count);
    } catch (error) {
      /* keep "other" */
    }
    return forms[category] || forms.other;
  }

  /* One request per URL for the whole page. blocks/bt-review-summary.liquid keeps its promise in the
     same object, so when the rating next to the title is on the page the plate reuses its answer. */
  function summaryRequest(url) {
    var cache = (window.__btReviewsSummary = window.__btReviewsSummary || {});
    if (!cache[url]) {
      cache[url] = fetch(url).then(function (response) {
        return response.json();
      });
    }
    return cache[url];
  }

  class BtSlideRating extends HTMLElement {
    connectedCallback() {
      if (this.asked) return;
      this.asked = true;

      /* Same shop, same product scope and same rounding as the rating next to the title — the two
         numbers must never disagree. Without that block the plate uses its own attributes. */
      var summary = document.querySelector('[data-bt-rsum]');
      var config = summary || this;
      var base = config.getAttribute('data-api-base') || 'https://lost-orders.vercel.app';
      var storeId = config.getAttribute('data-store-id') || '1';
      var store = config.getAttribute('data-store') || 'ua';
      var handle = config.getAttribute('data-handle') || '';

      var offset = 0;
      var floor = 0;
      if (summary) {
        offset = parseFloat(summary.getAttribute('data-rating-offset')) || 0;
        floor = parseFloat(summary.getAttribute('data-rating-min'));
        if (isNaN(floor)) floor = 4.0;
      }

      /* Built exactly like the summary block builds it — the URL is the cache key. */
      var url =
        base + '/api/public/reviews/summary?storeId=' + encodeURIComponent(storeId) + '&store=' + encodeURIComponent(store);
      if (handle) url += '&handle=' + encodeURIComponent(handle);

      summaryRequest(url)
        .then((data) => {
          if (!data || !data.success || !data.count) return;

          var average = parseFloat(data.average) + offset;
          if (isNaN(average)) return;
          if (average < floor) average = floor;
          if (average > 5) average = 5;

          var count = parseInt(data.count, 10);
          this.fill('[data-bt-rating-avg]', (Math.round(average * 10) / 10).toFixed(1));
          this.fill('[data-bt-rating-count]', String(count));
          this.fill('[data-bt-rating-word]', reviewsWord(this.getAttribute('data-locale'), count));
          this.hidden = false;
        })
        .catch(function () {
          /* API is down: the plate simply stays hidden. */
        });
    }

    fill(selector, text) {
      var node = this.querySelector(selector);
      if (node) node.textContent = text;
    }
  }

  if (!customElements.get('bt-slide-video')) customElements.define('bt-slide-video', BtSlideVideo);
  if (!customElements.get('bt-slide-rating')) customElements.define('bt-slide-rating', BtSlideRating);
})();
